<script>
	import SidebarLink from "@/pages/layouts/partials/sidebar-link.vue";
	import SidebarDropdown from "@/pages/layouts/partials/sidebar-dropdown.vue";
	import MenuService from '@/helpers/MenuService';
	import EditModal from '@/pages/layouts/partials/sidebar/edit.vue';

	export default {
		components: {
			SidebarDropdown, SidebarLink, EditModal,
		},
		data() {
			return {
				key: Math.random(),
				initialized: false,
				entries: [],
				collapsed: localStorage.getItem('sidebar-collapsed') === 'true',
				mobileOpen: false
			}
		},

		async mounted() {
			this.initialized = false;

			await MenuService.init(this.$page.props.menu_last_updated_at);

			this.initialized = true;

			this.initialize();

			this.addEventListenersToMenu();
		},

		methods: {
			toggleCollapse() {
				if (window.innerWidth < 992) {
					this.mobileOpen = !this.mobileOpen;
				} else {
					this.collapsed = !this.collapsed;
					localStorage.setItem('sidebar-collapsed', this.collapsed);
				}
			},

			closeMobileMenu() {
				this.mobileOpen = false;
			},

			async initialize(firstLoad = false) {
				this.entries = this.getMenuItems();
				await this.$nextTick();
				this.key = Math.random();
				await this.$nextTick();
				this.addEventListenersToMenu();
			},

			addEventListenersToMenu() {
				$(document)
					.off('.sidebar')
					.on('wwdev.menu.updated.sidebar', this.initialize.bind(this))
					.on('wwdev.toggleSidebar.sidebar', this.toggleCollapse.bind(this))
					.on('wwdev.closeMobileMenu.sidebar', this.closeMobileMenu.bind(this));
			},

			getMenuItems() {
				return MenuService.getEntries();
			},

			getAllRoutes() {
				const routes = [];
				for (const entry of this.entries) {
					if (entry.is_folder && entry.children) {
						for (const child of entry.children) {
							if (child.menuItem?.route) routes.push(child.menuItem.route);
						}
					} else if (entry.menuItem?.route) {
						routes.push(entry.menuItem.route);
					}
				}
				return routes;
			},

			isActive(route) {
				if (!route) return false;
				const currentPath = window.location.pathname;
				if (currentPath === route) return true;

				const allRoutes = this.getAllRoutes();
				const hasExactMatch = allRoutes.some(r => r === currentPath);
				if (hasExactMatch) return false;

				return currentPath.startsWith(route + '/');
			},

			isFolderActive(entry) {
				if (!entry.children) return false;
				return entry.children.some(child => this.isActive(child.menuItem?.route));
			},

			openFlyout(event) {
				if (!this.collapsed) return;
				const li = event.currentTarget;

				// Cancel any pending close for this item
				if (li._flyoutHideTimeout) {
					clearTimeout(li._flyoutHideTimeout);
					li._flyoutHideTimeout = null;
				}

				// Sluit alle andere open flyouts direct zodat er max 1 open is
				const others = li.parentElement?.querySelectorAll(':scope > li.flyout-open') || [];
				others.forEach(other => {
					if (other === li) return;
					if (other._flyoutHideTimeout) {
						clearTimeout(other._flyoutHideTimeout);
						other._flyoutHideTimeout = null;
					}
					other.classList.remove('flyout-open');
				});

				li.classList.add('flyout-open');

				const rect = li.getBoundingClientRect();
				let top = rect.top - 7;

				li.style.setProperty('--flyout-top', `${rect.top}px`);
				li.style.setProperty('--flyout-height', `${rect.height}px`);

				requestAnimationFrame(() => {
					const flyout = li.querySelector('.dropdown-menu') || li.querySelector('.hide-menu');
					if (!flyout) return;

					const flyoutRect = flyout.getBoundingClientRect();
					const viewportHeight = window.innerHeight;

					if (flyoutRect.bottom > viewportHeight) {
						top = top - (flyoutRect.bottom - viewportHeight) - 10;
						li.style.setProperty('--flyout-top', `${top + 7}px`);
					}
				});
			},

			closeFlyout(event) {
				if (!this.collapsed) return;
				const li = event.currentTarget;

				if (li._flyoutHideTimeout) {
					clearTimeout(li._flyoutHideTimeout);
				}

				li._flyoutHideTimeout = setTimeout(() => {
					li.classList.remove('flyout-open');
					li._flyoutHideTimeout = null;
				}, 300);
			}
		}
	}
</script>

<template>
	<aside :key="key" class="shadow-lg bg-white" :class="{'sidebar-collapsed': collapsed, 'sidebar-mobile-open': mobileOpen}">
        <div class="container-fluid py-container">
			<nav class="sidebar-nav">
                <div class="d-none d-lg-flex mb-3" :class="collapsed ? '' : 'justify-content-end'">
                    <button type="button" class="btn btn-circle btn-primary" @click="toggleCollapse">
                        <i class="bi bi-list"></i>
                    </button>
                </div>

				<ul class="sidebar-nav-list list-unstyled m-0 p-0">
					<template v-for="entry in entries" :key="entry.id">
						<template v-if="entry.is_folder">
							<SidebarDropdown :active="isFolderActive(entry)" @mouseenter="openFlyout" @mouseleave="closeFlyout">
								<template #icon>
									<i v-if="entry.icon" class="menuitem-icon" :class="entry.icon"></i>
								</template>

								<template #label>{{ entry.label }}</template>

								<template v-for="child in entry.children" :key="child.id">
									<SidebarLink :url="child.menuItem.route" :active="isActive(child.menuItem?.route)">
										<template #icon>
											<i v-if="child.icon || child.menuItem.icon" class="menuitem-icon" :class="child.icon || child.menuItem.icon"></i>
										</template>
										<template #label>{{ child.label || child.menuItem.label }}</template>
									</SidebarLink>
								</template>
							</SidebarDropdown>
						</template>

						<template v-else>
							<SidebarLink :url="entry.menuItem.route" :active="isActive(entry.menuItem?.route)" @mouseenter="openFlyout" @mouseleave="closeFlyout">
								<template #icon>
									<i v-if="entry.icon || entry.menuItem.icon" class="menuitem-icon" :class="entry.icon || entry.menuItem.icon"></i>
								</template>
								<template #label>{{ entry.label || entry.menuItem.label }}</template>
							</SidebarLink>
						</template>
					</template>
				</ul>
			</nav>
        </div>

		<button @click="event => $refs.modal.open()" type="button"
			class="menu-config-button rounded-0 btn btn-primary p-2 w-100 text-center"
			title="Menu aanpassen">
			<i class="bi bi-gear lh-1"></i>
			<span class="config-button-text ps-2">Menu aanpassen</span>
		</button>
	</aside>

	<EditModal v-if="initialized" ref="modal" />
</template>